import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { buildApiUrl } from "@/config/api";
import { useUserInfo, useUserToken } from "@/store/userStore";
import { Button } from "@/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/ui/form";
import { Input } from "@/ui/input";

type ChangePasswordFormData = {
	password: string;
	new_password: string;
	new_password_confirmation: string;
};

type ChangePasswordDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
};

export default function ChangePasswordDialog({
	open,
	onOpenChange,
}: ChangePasswordDialogProps) {
	const userInfo = useUserInfo();
	const userToken = useUserToken();
	const [isSubmitting, setIsSubmitting] = useState(false);

	const form = useForm<ChangePasswordFormData>({
		defaultValues: {
			password: "",
			new_password: "",
			new_password_confirmation: "",
		},
	});

	const handleSubmit = async (data: ChangePasswordFormData) => {
		setIsSubmitting(true);

		try {
			const response = await fetch(buildApiUrl("/change-password"), {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: `Bearer ${userToken.accessToken}`,
				},
				body: JSON.stringify({
					user_id: parseInt(userInfo.id || "0", 10),
					password: data.password,
					new_password: data.new_password,
					new_password_confirmation: data.new_password_confirmation,
				}),
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(
					errorData.message || "Hiba történt a jelszó módosítása során",
				);
			}

			const result = await response.json();

			toast.success(result.message || "A jelszó sikeresen módosítva", {
				closeButton: true,
			});

			form.reset();
			onOpenChange(false);
		} catch (error) {
			const errorMessage =
				error instanceof Error
					? error.message
					: "Hiba történt a jelszó módosítása során";
			toast.error(errorMessage, {
				closeButton: true,
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Jelszó módosítása</DialogTitle>
					<DialogDescription>
						Adja meg a jelenlegi jelszavát és az új jelszót
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
						<FormField
							control={form.control}
							name="password"
							rules={{
								required: "A jelenlegi jelszó megadása kötelező",
								minLength: {
									value: 8,
									message: "A jelszónak legalább 8 karakternek kell lennie",
								},
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Jelenlegi jelszó</FormLabel>
									<FormControl>
										<Input
											type="password"
											placeholder="Jelenlegi jelszó"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="new_password"
							rules={{
								required: "Az új jelszó megadása kötelező",
								minLength: {
									value: 8,
									message: "A jelszónak legalább 8 karakternek kell lennie",
								},
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Új jelszó</FormLabel>
									<FormControl>
										<Input
											type="password"
											placeholder="Új jelszó"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="new_password_confirmation"
							rules={{
								required: "Az új jelszó megerősítése kötelező",
								validate: (value) =>
									value === form.getValues("new_password") ||
									"A jelszavak nem egyeznek",
							}}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Új jelszó megerősítése</FormLabel>
									<FormControl>
										<Input
											type="password"
											placeholder="Új jelszó megerősítése"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									form.reset();
									onOpenChange(false);
								}}
								disabled={isSubmitting}
							>
								Mégse
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? "Módosítás..." : "Módosítás"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
